/**
 * 知识档案页面
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Plus, FileText, Trash2, Edit, Eye, X, Check, Loader2,
  Upload, AlertTriangle, RotateCcw, ChevronDown, ChevronUp,
  FileImage, FileVideo, FileAudio, FileCode, FileSpreadsheet, File, FileArchive,
  Sparkles, Archive, ArchiveRestore, Folder, FolderPlus, FileDown, ChevronRight, Pencil as PencilIcon, ArrowUp, MoreVertical, FolderInput,
} from 'lucide-react';
import type { KnowledgeArchive, KnowledgeArchiveFile, QualityIncidentDetail, ExtractionResult } from '@/types/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmModal';
import { api, getErrorMessage } from '@/services/api';
import FileUpload from '@/components/FileUpload';
import QualityExtractionModal from '@/components/QualityExtractionModal';
import styles from './Knowledge.module.css';

// 经典 Windows 黄色立体文件夹图标
const WinFolderIcon = ({ size = 56 }: { size?: number }) => (
  <svg width={size} height={size * 0.82} viewBox="0 0 64 52" fill="none" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,.18))' }}>
    <path d="M2 8 C2 4.7 4.7 2 8 2 H22 L30 10 H56 C59.3 10 62 12.7 62 16 V44 C62 47.3 59.3 50 56 50 H8 C4.7 50 2 47.3 2 44 Z" fill="url(#wfBack)" />
    <path d="M2 18 H62 V44 C62 47.3 59.3 50 56 50 H8 C4.7 50 2 47.3 2 44 Z" fill="url(#wfFront)" />
    <path d="M2 16 H62 V20 H2 Z" fill="#C98A2D" opacity="0.35" />
    <defs>
      <linearGradient id="wfBack" x1="0" y1="0" x2="0" y2="1">
        <stop stopColor="#FFD86B" /><stop offset="1" stopColor="#EFA23F" />
      </linearGradient>
      <linearGradient id="wfFront" x1="0" y1="0" x2="0" y2="1">
        <stop stopColor="#FFE58F" /><stop offset="1" stopColor="#F6BE4F" />
      </linearGradient>
    </defs>
  </svg>
);

interface ArchiveWithTags extends KnowledgeArchive {
  tags: string[];
  uploader_name?: string;
}

// 获取文件类型对应的图标组件
const getFileIcon = (type: string) => {
  const t = type.toLowerCase();
  const iconProps = { size: 14, strokeWidth: 1.5 };

  // PDF
  if (t === 'pdf') return <FileText {...iconProps} className={styles.fileTypeIcon} style={{ color: '#e53e3e' }} />;
  // Word 文档
  if (['doc', 'docx', 'word', 'document'].includes(t)) return <FileText {...iconProps} className={styles.fileTypeIcon} style={{ color: '#3182ce' }} />;
  // Excel 表格
  if (['xls', 'xlsx', 'excel', 'spreadsheet'].includes(t)) return <FileSpreadsheet {...iconProps} className={styles.fileTypeIcon} style={{ color: '#38a169' }} />;
  // PPT 演示
  if (['ppt', 'pptx', 'powerpoint', 'presentation'].includes(t)) return <FileText {...iconProps} className={styles.fileTypeIcon} style={{ color: '#dd6b20' }} />;
  // 视频
  if (['mp4', 'avi', 'mov', 'mkv', 'video', 'wmv', 'flv'].includes(t)) return <FileVideo {...iconProps} className={styles.fileTypeIcon} style={{ color: '#805ad5' }} />;
  // 图片
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'image', 'bmp', 'svg'].includes(t)) return <FileImage {...iconProps} className={styles.fileTypeIcon} style={{ color: '#d69e2e' }} />;
  // 音频
  if (['mp3', 'wav', 'ogg', 'audio', 'flac', 'aac'].includes(t)) return <FileAudio {...iconProps} className={styles.fileTypeIcon} style={{ color: '#d53f8c' }} />;
  // 文本
  if (['txt', 'text', 'md', 'markdown'].includes(t)) return <FileText {...iconProps} className={styles.fileTypeIcon} style={{ color: '#718096' }} />;
  // CSV
  if (['csv'].includes(t)) return <FileText {...iconProps} className={styles.fileTypeIcon} style={{ color: '#38a169' }} />;
  // 压缩包
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(t)) return <FileArchive {...iconProps} className={styles.fileTypeIcon} style={{ color: '#975a16' }} />;
  // 代码
  if (['js', 'ts', 'py', 'java', 'cpp', 'c', 'go', 'rs', 'code'].includes(t)) return <FileCode {...iconProps} className={styles.fileTypeIcon} style={{ color: '#5a67d8' }} />;
  return <File {...iconProps} className={styles.fileTypeIcon} style={{ color: '#718096' }} />;
};

// 格式化文件大小
const formatSize = (bytes: number | null) => {
  if (bytes === null || bytes === undefined) return '未知大小';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

export default function KnowledgePage() {
  const { hasPermission, user } = useAuth();
  const { showToast } = useToast();
  const confirmDialog = useConfirm();
  const location = useLocation();

  // 根据 URL 路径判断功能：学习考试 / 知识传承
  // 知识空间：daily=日常档案 / quality=质量档案（仅知识传承模式使用）
  const [space, setSpace] = useState<string>(() => localStorage.getItem('knowledge_space') || 'daily');
  const isExam = location.pathname.startsWith('/exam');
  const pageTitle = isExam ? '学习档案' : '知识档案';
  // 知识传承支持的档案类型（四类起步，可按需扩展）；学习考试固定 general
  // 类型选项按空间联动：日常空间只有三种日常类型；质量空间锁定质量事故
  const KNOWLEDGE_TYPES = space === 'quality'
    ? [{ value: 'quality_incident', label: '质量事故' }]
    : [
        { value: 'handbook', label: '制度规范' },
        { value: 'product', label: '产品技术' },
        { value: 'material', label: '材料说明' },
        { value: 'quality_incident', label: '质量事故' },
      ];
  const TYPE_LABELS: Record<string, string> = {
    handbook: '制度规范', product: '产品技术', material: '材料说明',
    quality_incident: '质量事故', general: '学习档案',
  };
  // 各类型默认可见性（表单默认值，维护者可改）
  const TYPE_DEFAULT_VIS: Record<string, number> = {
    handbook: 2, product: 2, material: 2, quality_incident: 0, general: 0,
  };
  const filterArchiveType = isExam ? 'general' : 'quality_incident';
  const showQualityFeatures = !isExam;  // 知识传承才显示 AI 解析等质量事故功能

  const canEdit = hasPermission(isExam ? 'exam:知识管理' : 'knowledge:知识管理');

  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8; // 每页显示8条记录

  const [archives, setArchives] = useState<ArchiveWithTags[]>([]);
  const [uploaders, setUploaders] = useState<string[]>([]); // 上传用户列表
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterUploader, setFilterUploader] = useState<string>('all'); // 上传用户筛选
  const [filterStartDate, setFilterStartDate] = useState<string>(''); // 开始日期
  const [filterEndDate, setFilterEndDate] = useState<string>(''); // 结束日期
  const [filterVisibility, setFilterVisibility] = useState<string>('all'); // 可见性筛选
  const [filterType, setFilterType] = useState<string>('all'); // 类型筛选（仅知识传承）
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedArchive, setSelectedArchive] = useState<ArchiveWithTags | null>(null);
  const [editingArchive, setEditingArchive] = useState<ArchiveWithTags | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    archive_name: '',
    archive_desc: '',
    visibility: 0,
    archive_type: filterArchiveType as string,
    shared_to_knowledge: 0,
    is_archived: 0,
    folder_id: null as number | null,
  });
  const [archiveFiles, setArchiveFiles] = useState<KnowledgeArchiveFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState<number | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    fileId: number | null;
    fileName: string;
  }>({ show: false, fileId: null, fileName: '' });

  // 编辑模式下的临时操作追踪
  const [pendingDeleteFiles, setPendingDeleteFiles] = useState<number[]>([]); // 待删除的文件ID列表
  const [newlyUploadedFiles, setNewlyUploadedFiles] = useState<number[]>([]); // 新上传的文件ID列表
  const [originalFiles, setOriginalFiles] = useState<KnowledgeArchiveFile[]>([]); // 编辑开始时的原始文件列表

  // 多选相关状态
  const [selectedArchives, setSelectedArchives] = useState<number[]>([]);
  const [isBatchDeleteModalOpen, setIsBatchDeleteModalOpen] = useState(false);

  // 展开的档案ID列表
  const [expandedArchives, setExpandedArchives] = useState<number[]>([]);

  // 质量事故提取状态
  const [extractingId, setExtractingId] = useState<number | null>(null);
  const [showExtractionModal, setShowExtractionModal] = useState(false);
  const [extractionData, setExtractionData] = useState<{
    incident_id: number;
    merge_result?: ExtractionResult | null;
    file_results: ExtractionResult[];
  } | null>(null);
  const [extractionIncidentId, setExtractionIncidentId] = useState<number | null>(null);
  const [showDetailModal2, setShowDetailModal2] = useState(false);
  const [detailData, setDetailData] = useState<QualityIncidentDetail | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // ==================== 文件夹体系（仅知识传承模式） ====================
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null); // null=根目录
  const [folders, setFolders] = useState<any[]>([]);
  const [breadcrumb, setBreadcrumb] = useState<any[]>([]);
  const [folderModal, setFolderModal] = useState<{ mode: 'create' | 'rename' | 'move'; folder?: any } | null>(null);
  const [newMenuOpen, setNewMenuOpen] = useState(false); // “新建”下拉菜单
  const [folderForm, setFolderForm] = useState({ folder_name: '', parent_id: null as number | null });
  const [folderSaving, setFolderSaving] = useState(false);
  const [allFolders, setAllFolders] = useState<any[]>([]); // 全部文件夹（父级选择用，扁平）

  const loadFolders = useCallback(async () => {
    if (isExam) return;
    try {
      const [childRes, pathRes] = await Promise.all([
        api.listKnowledgeFolders(space, currentFolderId),
        currentFolderId ? api.getKnowledgeFolderPath(currentFolderId) : Promise.resolve({ data: [] }),
      ]);
      setFolders(childRes.data || []);
      setBreadcrumb(pathRes.data || []);
      // 拉取全空间文件夹（扁平，用于"移动到"选择）：递归拉全部子级
      const all: any[] = [];
      const walk = async (parentId: number | null) => {
        const res = await api.listKnowledgeFolders(space, parentId);
        for (const f of (res.data || [])) {
          all.push(f);
          await walk(f.folder_id);
        }
      };
      await walk(null);
      setAllFolders(all);
    } catch (err) {
      console.error('加载文件夹失败:', err);
    }
  }, [isExam, space, currentFolderId]);

  useEffect(() => { loadFolders(); }, [loadFolders]);

  // 搜索防抖（300ms）
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // 档案列表随 当前文件夹/空间/搜索词 自动刷新（进入文件夹 = 只看文件夹内的档案）
  useEffect(() => {
    if (isExam) return;
    setCurrentPage(1);
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolderId, space, debouncedSearch]);

  // 文件夹 → 完整路径（父级 / 子级），下拉选项区分同名子文件夹用
  const folderPathMap = useMemo(() => {
    const byId = new Map(allFolders.map(f => [f.folder_id, f]));
    const pathOf = (f: any): string => {
      const names = [f.folder_name];
      let p = f.parent_id;
      const seen = new Set([f.folder_id]);
      while (p && byId.has(p) && !seen.has(p)) {
        seen.add(p);
        const parent = byId.get(p);
        names.unshift(parent.folder_name);
        p = parent.parent_id;
      }
      return names.join(' / ');
    };
    const m = new Map<number, string>();
    allFolders.forEach(f => m.set(f.folder_id, pathOf(f)));
    return m;
  }, [allFolders]);

  const switchSpace = (newSpace: string) => {
    if (newSpace === space) return;
    setSpace(newSpace);
    localStorage.setItem('knowledge_space', newSpace);
    setCurrentFolderId(null);
  };

  const handleFolderSave = async () => {
    if (!folderModal) return;
    setFolderSaving(true);
    try {
      if (folderModal.mode === 'create') {
        await api.createKnowledgeFolder({
          folder_name: folderForm.folder_name.trim(),
          space,
          parent_id: folderModal.folder ? folderModal.folder.folder_id : currentFolderId,
        });
        showToast('success', '文件夹已创建');
      } else if (folderModal.mode === 'rename' && folderModal.folder) {
        await api.updateKnowledgeFolder(folderModal.folder.folder_id, { folder_name: folderForm.folder_name.trim() });
        showToast('success', '文件夹已重命名');
      } else if (folderModal.mode === 'move' && folderModal.folder) {
        await api.updateKnowledgeFolder(folderModal.folder.folder_id, { parent_id: folderForm.parent_id ?? undefined });
        showToast('success', '文件夹已移动');
      }
      setFolderModal(null);
      loadFolders();
    } catch (err) {
      showToast('error', getErrorMessage(err));
    } finally {
      setFolderSaving(false);
    }
  };

  const handleFolderDelete = async (folder: any) => {
    const ok = await confirmDialog.confirm({ title: '确认删除', message: `确定删除文件夹「${folder.folder_name}」？`, confirmText: '删除', danger: true });
    if (!ok) return;
    try {
      await api.deleteKnowledgeFolder(folder.folder_id);
      showToast('success', '已删除');
      loadFolders();
    } catch (err) {
      showToast('error', getErrorMessage(err));
    }
  };

  // “更多”悬浮菜单（下载/归档收纳；悬浮打开、移出延时 150ms 关闭）
  const [moreMenu, setMoreMenu] = useState<{ id: number; x: number; y: number } | null>(null);
  const moreCloseTimer = useRef<any>(null);
  const openMoreMenu = (archiveId: number, e: React.MouseEvent) => {
    if (moreCloseTimer.current) clearTimeout(moreCloseTimer.current);
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMoreMenu({ id: archiveId, x: r.right - 128, y: r.bottom + 4 });
  };
  const scheduleCloseMoreMenu = () => {
    moreCloseTimer.current = setTimeout(() => setMoreMenu(null), 150);
  };
  const cancelCloseMoreMenu = () => { if (moreCloseTimer.current) clearTimeout(moreCloseTimer.current); };

  // 档案移动到文件夹（“更多”菜单入口）
  const [moveArchive, setMoveArchive] = useState<ArchiveWithTags | null>(null);
  const [moveTargetFolder, setMoveTargetFolder] = useState<number | null>(null);
  const [moveSaving, setMoveSaving] = useState(false);

  const handleMoveArchive = async () => {
    if (!moveArchive || moveSaving) return;
    setMoveSaving(true);
    try {
      await api.updateKnowledgeArchive(moveArchive.archive_id, {
        folder_id: moveTargetFolder ?? 0,
        archive_type: (moveArchive as any).archive_type,
      } as any);
      showToast('success', `已移动到${moveTargetFolder ? '指定文件夹' : '未分类（根目录）'}`);
      setMoveArchive(null);
      loadData(true);
    } catch (err) {
      showToast('error', getErrorMessage(err));
    } finally {
      setMoveSaving(false);
    }
  };

  // 档案下载（质量空间由后端拦截 + 前端不显示按钮）
  const handleDownloadArchive = async (archive: ArchiveWithTags) => {
    try {
      showToast('info', '开始下载...');
      await api.downloadKnowledgeArchive(archive.archive_id);
    } catch (err) {
      showToast('error', getErrorMessage(err));
    }
  };

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const archivesRes = await api.getKnowledgeArchives({
        page: 1, page_size: 100,
        ...( !isExam ? { space } : {}),
        ...( !isExam && !debouncedSearch ? { folder_id: currentFolderId ?? 0 } : {}), // 搜索时全局检索（结果带 folder_path）
      });

      // 处理档案数据
      if (archivesRes && archivesRes.data) {
        const archivesData = Array.isArray(archivesRes.data) ? archivesRes.data : [];
        setArchives(archivesData.map(a => ({
          ...a,
          tags: Array.isArray(a.tags) ? a.tags : [],
          uploader_name: a.uploader_name || '未知',
        })));
        // 提取上传用户列表
        const uniqueUploaders = [...new Set(archivesData.map(a => a.uploader_name).filter((name): name is string => Boolean(name)))];
        setUploaders(uniqueUploaders);
      }
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // ==================== 知识处理（向量化，仅知识传承模式） ====================

  // 触发知识处理：成功后本地置为处理中，轮询自动接管刷新
  const handleProcessArchive = async (archiveId: number) => {
    try {
      const res = await api.processKnowledge(archiveId);
      showToast('success', res.message || '已开始处理');
      setArchives(prev => prev.map(a => a.archive_id === archiveId ? { ...a, process_status: 1 } : a));
    } catch (err) {
      showToast('error', getErrorMessage(err));
    }
  };

  // 列表中存在处理中的档案时，每 3 秒静默刷新（完成/失败后自动停止）
  const anyProcessing = showQualityFeatures && archives.some(a => a.process_status === 1);
  useEffect(() => {
    if (!anyProcessing) return;
    const t = setInterval(() => loadData(true), 3000);
    return () => clearInterval(t);
  }, [anyProcessing]);

  const filteredArchives = archives.filter((archive) => {
    // 搜索文本过滤
    const matchSearch =
      archive.archive_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      archive.archive_desc?.toLowerCase().includes(searchQuery.toLowerCase());
    // 上传用户过滤
    const matchUploader = filterUploader === 'all' || archive.uploader_name === filterUploader;
    // 上传时间区间过滤
    let matchDate = true;
    if (archive.upload_time) {
      const uploadDate = new Date(archive.upload_time);
      const uploadDateStr = uploadDate.toISOString().split('T')[0];
      if (filterStartDate && uploadDateStr < filterStartDate) {
        matchDate = false;
      }
      if (filterEndDate && uploadDateStr > filterEndDate) {
        matchDate = false;
      }
    }
    // 可见性过滤
    const matchVisibility = filterVisibility === 'all' || String(archive.visibility) === filterVisibility;
    // 档案类型过滤：
    // 学习考试模式只显示 general；
    // 知识传承模式显示 日常类型(handbook/product/material/quality_incident) ∪ 共享的 general
    const at = (archive as any).archive_type as string;
    const shared = (archive as any).shared_to_knowledge === 1;
    let matchType: boolean;
    if (isExam) {
      matchType = at === 'general';
    } else {
      const inKnowledge = at !== 'general' || shared;
      matchType = inKnowledge && (filterType === 'all' || at === filterType);
    }
    return matchSearch && matchUploader && matchDate && matchVisibility && matchType;
  });

  // 分页计算
  const totalPages = Math.ceil(filteredArchives.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedArchives = filteredArchives.slice(startIndex, startIndex + itemsPerPage);

  // 切换档案展开/收起（显示文件列表）
  const toggleArchiveExpand = (archive_id: number) => {
    setExpandedArchives((prev) =>
      prev.includes(archive_id)
        ? prev.filter((id) => id !== archive_id)
        : [...prev, archive_id]
    );
  };

  // 归档/恢复档案：归档后不参与问答检索
  const handleToggleArchive = async (archive: ArchiveWithTags) => {
    const target = (archive as any).is_archived === 1 ? 0 : 1;
    try {
      await api.updateKnowledgeArchive(archive.archive_id, {
        is_archived: target,
        archive_type: (archive as any).archive_type,
      } as any);
      showToast('success', target === 1 ? '已归档（不再参与问答检索）' : '已恢复');
      loadData(true);
    } catch (err) {
      showToast('error', getErrorMessage(err));
    }
  };

  const handleCreate = () => {
    setEditingArchive(null);
    const defaultType = !isExam && space === 'quality' ? 'quality_incident' : filterArchiveType;
    setFormData({
      archive_name: '', archive_desc: '',
      visibility: TYPE_DEFAULT_VIS[defaultType] ?? 0,
      archive_type: defaultType,
      shared_to_knowledge: 0, is_archived: 0,
      folder_id: currentFolderId,
    });
    setShowModal(true);
  };

  const handleEdit = async (archive: ArchiveWithTags) => {
    setEditingArchive(archive);
    setFormData({
      archive_name: archive.archive_name,
      archive_desc: archive.archive_desc || '',
      visibility: archive.visibility ?? 0,
      archive_type: (archive as any).archive_type || filterArchiveType,
      shared_to_knowledge: (archive as any).shared_to_knowledge ?? 0,
      is_archived: (archive as any).is_archived ?? 0,
      folder_id: (archive as any).folder_id ?? null,
    });
    // 清空临时操作追踪
    setPendingDeleteFiles([]);
    setNewlyUploadedFiles([]);
    setShowModal(true);
    // 加载已上传的文件
    await loadArchiveFiles(archive.archive_id, true);
  };

  const loadArchiveFiles = async (archive_id: number, isInitialLoad = false) => {
    setLoadingFiles(true);
    try {
      const res = await api.getArchiveFiles(archive_id);
      if (res.code === 200 && res.data) {
        setArchiveFiles(res.data);
        // 如果是编辑开始时的初始加载，保存原始文件列表
        if (isInitialLoad) {
          setOriginalFiles(res.data);
        }
      }
    } catch (error) {
      console.error('加载文件失败:', error);
      setArchiveFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleDeleteFile = async (fileId: number, fileName: string) => {
    // 打开确认弹窗
    setConfirmModal({ show: true, fileId, fileName });
  };

  // 确认删除文件（只标记，不立即删除）
  const confirmDeleteFile = async () => {
    const fileId = confirmModal.fileId;
    if (!fileId) return;

    // 添加到待删除列表
    setPendingDeleteFiles(prev => [...prev, fileId]);
    // 从显示列表中移除（但不会真正删除，除非点击保存）
    setArchiveFiles(prev => prev.filter(f => f.file_id !== fileId));
    setConfirmModal({ show: false, fileId: null, fileName: '' });
  };

  // 取消编辑时的处理函数
  const handleCancelEdit = async () => {
    console.log('handleCancelEdit called, editingArchive:', editingArchive);
    console.log('newlyUploadedFiles:', newlyUploadedFiles);
    console.log('pendingDeleteFiles:', pendingDeleteFiles);

    if (editingArchive) {
      // 如果有新上传的文件，需要删除它们（因为用户取消了）
      if (newlyUploadedFiles.length > 0) {
        console.log('准备删除新上传的文件:', newlyUploadedFiles);
        let successCount = 0;
        let failCount = 0;
        for (const fileId of newlyUploadedFiles) {
          try {
            await api.deleteFile(fileId);
            console.log('取消编辑，成功删除新上传的文件:', fileId);
            successCount++;
          } catch (error) {
            console.error('删除新上传文件失败:', fileId, error);
            failCount++;
          }
        }
        if (failCount === 0) {
          showToast('info', `已撤销上传的 ${successCount} 个文件`);
        } else if (successCount === 0) {
          showToast('error', `撤销上传文件失败（${failCount} 个）`);
        } else {
          showToast('warning', `已撤销 ${successCount} 个文件，${failCount} 个失败`);
        }
      }
      // 恢复原始文件列表
      setArchiveFiles(originalFiles);
      // 清空临时操作追踪
      setPendingDeleteFiles([]);
      setNewlyUploadedFiles([]);
    }
    setShowModal(false);
  };

  // 关闭确认弹窗
  const closeConfirmModal = () => {
    setConfirmModal({ show: false, fileId: null, fileName: '' });
  };

  const handleDelete = async (archive_id: number) => {
    const confirmed = await confirmDialog.confirm({
      title: '确认删除',
      message: '确定要删除「' + (archives.find(a => a.archive_id === archive_id)?.archive_name || '') + '」吗？\n\n删除后将同时清除：\n· 档案中的所有文件\n· 关联的质量事故记录\n· AI 解析提取的数据\n\n此操作不可恢复！',
      confirmText: '确认删除',
      danger: true,
    });
    if (!confirmed) return;

    try {
      await api.deleteKnowledgeArchive(archive_id);
      setArchives(archives.filter((a) => a.archive_id !== archive_id));
      showToast('success', '删除成功');
    } catch (error: unknown) {
      showToast('error', getErrorMessage(error) || '删除失败');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.archive_name.trim()) {
      showToast('warning', '请输入档案名称');
      return;
    }

    setSaving(true);
    try {
      if (editingArchive) {
        // 更新档案信息
        await api.updateKnowledgeArchive(editingArchive.archive_id, {
          archive_name: formData.archive_name.trim(),
          archive_desc: formData.archive_desc.trim() || undefined,
          tags: [],
          visibility: formData.visibility,
          archive_type: formData.archive_type,
          shared_to_knowledge: formData.shared_to_knowledge,
          folder_id: formData.folder_id ?? 0,
        });

        // 执行所有待删除的文件删除操作
        if (pendingDeleteFiles.length > 0) {
          for (const fileId of pendingDeleteFiles) {
            try {
              await api.deleteFile(fileId);
            } catch (error) {
              console.error('删除文件失败:', fileId, error);
            }
          }
        }

        await loadData();
        setShowModal(false);
        // 清空临时操作追踪
        setPendingDeleteFiles([]);
        setNewlyUploadedFiles([]);
      } else {
        // 创建新档案
        const res = await api.createKnowledgeArchive(
          {
            archive_name: formData.archive_name.trim(),
            archive_desc: formData.archive_desc.trim() || undefined,
            tags: [],
            visibility: formData.visibility,
            archive_type: formData.archive_type,
            shared_to_knowledge: formData.shared_to_knowledge,
            folder_id: formData.folder_id ?? 0,
          },
          user?.user_id || 1
        );

        if (res.code === 200 && res.data) {
          const created = res.data;
          await loadData();
          // 创建成功后自动切换到编辑模式，显示文件上传区域
          setEditingArchive({
            archive_id: created.archive_id,
            archive_name: created.archive_name,
            archive_desc: created.archive_desc || '',
            uploader_id: created.uploader_id,
            uploader_name: created.uploader_name,
            upload_time: created.upload_time,
            status: created.status,
            update_time: created.update_time,
            visibility: created.visibility,
            files: created.files || [],
            tags: [],
          });
          setOriginalFiles([]);
          setPendingDeleteFiles([]);
          setNewlyUploadedFiles([]);
          setArchiveFiles([]);
          showToast('success', '创建成功，可上传文件');
        }
      }
    } catch (error: unknown) {
      showToast('error', getErrorMessage(error) || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  // 统一创建时间展示格式：YYYY-MM-DD（文件夹行与档案行共用）
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const formatDateSimple = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 多选相关函数
  const toggleArchiveSelection = (archive_id: number) => {
    setSelectedArchives(prev =>
      prev.includes(archive_id)
        ? prev.filter(id => id !== archive_id)
        : [...prev, archive_id]
    );
  };

  // 全选/取消全选当前页的档案
  const selectAllArchives = () => {
    const currentPageIds = paginatedArchives.map(a => a.archive_id);
    const allCurrentPageSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedArchives.includes(id));

    if (allCurrentPageSelected) {
      // 取消选择当前页的所有档案
      setSelectedArchives(selectedArchives.filter(id => !currentPageIds.includes(id)));
    } else {
      // 选择当前页的所有档案
      setSelectedArchives([...selectedArchives.filter(id => !currentPageIds.includes(id)), ...currentPageIds]);
    }
  };

  // ===== 质量事故 AI 提取 =====

  const [extractProgress, setExtractProgress] = useState<string>('');
  const [showExtractModal, setShowExtractModal] = useState(false);
  const [extractModalFiles, setExtractModalFiles] = useState<KnowledgeArchiveFile[]>([]);
  const [extractModalArchiveId, setExtractModalArchiveId] = useState<number | null>(null);
  const [extractModalArchiveName, setExtractModalArchiveName] = useState('');
  const [extractModalStage, setExtractModalStage] = useState<'confirm' | 'running' | 'done'>('confirm');
  const [extractModalIncidentId, setExtractModalIncidentId] = useState<number | null>(null);
  const [extractCompletedFiles, setExtractCompletedFiles] = useState(0);
  const extractPollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  // 打开提取确认弹窗
  const openExtractModal = async (archive_id: number) => {
    const archive = archives.find(a => a.archive_id === archive_id);
    if (!archive) return;
    setExtractModalArchiveId(archive_id);
    setExtractModalArchiveName(archive.archive_name);
    setExtractModalStage('confirm');

    // 加载档案文件列表
    try {
      const res = await api.getArchiveFiles(archive_id);
      if (res.code === 200 && res.data) {
        setExtractModalFiles(res.data);
      }
    } catch { setExtractModalFiles([]); }

    setShowExtractModal(true);
  };

  // 确认开始提取
  const confirmExtract = async () => {
    if (!extractModalArchiveId) return;
    setExtractModalStage('running');
    setExtractingId(extractModalArchiveId);

    try {
      const archive = archives.find(a => a.archive_id === extractModalArchiveId);
      const createRes = await api.createQualityIncident({ incident_name: archive?.archive_name, archive_id: extractModalArchiveId });
      const incidentId = createRes.data.incident_id;
      setExtractionIncidentId(incidentId);
      setExtractModalIncidentId(incidentId);

      // 发起提取（不 await）
      const extractPromise = api.extractQualityIncident(incidentId);
      setTimeout(async () => { try { await extractPromise; } catch {} }, 0);

      // 开始轮询进度
      const startTime = Date.now();
      extractPollRef.current = setInterval(async () => {
        try {
          const progRes = await api.getExtractionProgress(incidentId);
          const p = progRes.data;
          if (!p) return;
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          setExtractCompletedFiles(p.completed_files);
          if (p.current_file) {
            setExtractProgress(`正在解析 ${p.current_file}（${p.completed_files + 1}/${p.total_files}）已耗时 ${elapsed} 秒`);
          } else {
            setExtractProgress(`${p.message} 已耗时 ${elapsed} 秒`);
          }

          if (p.extraction_status === 2 || p.extraction_status === 3) {
            clearInterval(extractPollRef.current!);
            extractPollRef.current = null;
            setExtractModalStage('done');
            setExtractProgress('');
            setExtractingId(null);
            showToast('success', `提取完成：${p.completed_files}/${p.total_files} 个文件已解析`);
            const extRes = await api.getExtractionResults(incidentId);
            setExtractionData(extRes.data);
            setShowExtractionModal(true);
            loadData();
          }
          if (p.extraction_status === 4) {
            clearInterval(extractPollRef.current!);
            extractPollRef.current = null;
            setExtractModalStage('done');
            setExtractProgress('');
            setExtractingId(null);
            showToast('error', `提取失败：${p.extraction_error || '未知错误'}`);
          }
        } catch {}
      }, 2000);

      setTimeout(() => { if (extractPollRef.current) { clearInterval(extractPollRef.current); } }, 300000);
    } catch (err) { showToast('error', getErrorMessage(err)); setExtractingId(null); setExtractModalStage('confirm'); }
  };

  const handleConfirmExtraction = async () => {
    if (!extractionIncidentId || !extractionData) return;
    try {
      const sectionFields: Record<string,string[]> = {
        basic_info: ['incident_type','severity_level','discovery_stage','discovery_time','occurrence_time','closure_time','recorder_name','reviewer_name','source_report_path'],
        product_info: ['customer_name','product_name','product_type','spec_model','product_batch','order_no','total_quantity'],
        incident_scene: ['workshop','production_line','shift','process_stage','production_batch'],
        defect_details: ['defect_location','defect_category','detection_method','defect_detail','total_output','defect_quantity','disposal_method','rework_quantity','scrap_quantity','impact_scope'],
        losses: ['downtime_duration','total_loss','scrap_loss','rework_loss','customer_claim'],
        responsibility: ['responsible_dept','direct_responsible','indirect_responsible','mgmt_responsible','other_responsible','cause_category'],
        improvement: ['containment_action','improvement_project','improvement_category','planned_completion','actual_completion','main_dept','main_dept_head','related_depts','related_dept_heads','verify_dept','verifier','verify_evaluation'],
      };
      const data: any = { incident_id: extractionIncidentId };
      const merge = extractionData.merge_result?.merge_json || {};
      for (const [section, fields] of Object.entries(sectionFields)) {
        data[section] = {};
        for (const f of fields) data[section][f] = merge[f] ?? null;
      }
      await api.confirmExtraction(data);
      showToast('success', '确认保存成功');
      setShowExtractionModal(false);
      loadArchives();
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  const handleShowDetail = async (archive_id: number) => {
    try {
      const res = await api.getQualityIncidents({});
      const items = res.data?.items || [];
      const match = items.find((item: any) => item.archive_id === archive_id);
      if (match) {
        const detail = await api.getQualityIncidentDetail(match.incident_id);
        setDetailData(detail.data);
      } else {
        // 尚未解析，显示全空的详情弹窗
        const ALL_SECTION_FIELDS: Record<string, string[]> = {
          basic_info: ['incident_type','severity_level','discovery_stage','discovery_time','occurrence_time','closure_time','recorder_name','reviewer_name','source_report_path'],
          product_info: ['customer_name','product_name','product_type','spec_model','product_batch','order_no','total_quantity'],
          incident_scene: ['workshop','production_line','shift','process_stage','production_batch'],
          defect_details: ['defect_location','defect_category','detection_method','defect_detail','total_output','defect_quantity','disposal_method','rework_quantity','scrap_quantity','impact_scope'],
          losses: ['downtime_duration','total_loss','scrap_loss','rework_loss','customer_claim'],
          responsibility: ['responsible_dept','direct_responsible','indirect_responsible','mgmt_responsible','other_responsible','cause_category'],
          improvement: ['containment_action','improvement_project','improvement_category','planned_completion','actual_completion','main_dept','main_dept_head','related_depts','related_dept_heads','verify_dept','verifier','verify_evaluation'],
        };
        const emptySections: any = {};
        for (const [section, fields] of Object.entries(ALL_SECTION_FIELDS)) {
          emptySections[section] = {};
          for (const f of fields) emptySections[section][f] = null;
        }
        setDetailData({
          incident_id: 0, incident_no: '尚未解析', extraction_status: 0,
          files: [],
          ...emptySections,
        } as any);
      }
      setShowDetailModal2(true);
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  const handleBatchDelete = async () => {
    if (selectedArchives.length === 0) {
      showToast('warning', '请至少选择一个知识档案');
      return;
    }

    try {
      for (const archive_id of selectedArchives) {
        await api.deleteKnowledgeArchive(archive_id);
      }
      setArchives(archives.filter(a => !selectedArchives.includes(a.archive_id)));
      setSelectedArchives([]);
      setIsBatchDeleteModalOpen(false);
      showToast('success', `成功删除 ${selectedArchives.length} 个知识档案`);
    } catch (error: unknown) {
      showToast('error', getErrorMessage(error) || '批量删除失败');
    }
  };

  const openBatchDeleteModal = () => {
    setIsBatchDeleteModalOpen(true);
  };

  const closeBatchDeleteModal = () => {
    setIsBatchDeleteModalOpen(false);
  };

  if (loading && archives.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <Loader2 className={styles.spinner} size={32} />
          <span>加载中...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* 页头 */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>{pageTitle}</h1>
          <p className={styles.subtitle}>管理和维护知识库内容</p>
        </div>
      </div>

      {/* AI 提取进度提示 */}
      {extractProgress && (
        <div style={{background:'linear-gradient(135deg,#667eea,#764ba2)',color:'#fff',padding:'10px 20px',borderRadius:'8px',marginBottom:'12px',display:'flex',alignItems:'center',gap:'10px',fontSize:'14px'}}>
          <Loader2 size={18} className={styles.spin} />
          <span>{extractProgress}</span>
        </div>
      )}


      {/* 搜索栏（知识传承模式：标签页与搜索框同行） */}
      <div className={styles.searchBar} style={isExam ? undefined : { flexWrap: 'nowrap', gap: 12, marginBottom: 12 }}>
        {!isExam && <div style={{ display: 'inline-flex', background: '#f3f4f6', borderRadius: 10, padding: 3 }}>
            {[{ v: 'daily', l: '日常档案' }, { v: 'quality', l: '质量档案' }].map(o => (
              <button key={o.v} onClick={() => switchSpace(o.v)}
                style={{
                  border: 'none', cursor: 'pointer', padding: '6px 18px', borderRadius: 8,
                  fontSize: '.875rem', fontWeight: space === o.v ? 600 : 400,
                  background: space === o.v ? '#fff' : 'transparent',
                  color: space === o.v ? 'var(--cinnabar-600, #dc2626)' : '#6b7280',
                  boxShadow: space === o.v ? '0 1px 3px rgba(0,0,0,.08)' : 'none',
                }}>
                {o.l}
              </button>
            ))}
          </div>}
        <div className={styles.searchInput} style={isExam ? undefined : { width: 280, flex: '0 0 280px', minWidth: 200 }}>
          <Search size={18} strokeWidth={1.5} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="搜索知识档案..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className={styles.clearSearch} onClick={() => setSearchQuery('')}>
              <X size={16} strokeWidth={1.5} />
            </button>
          )}
        </div>
        <div className={styles.filters}>
          {/* 上传用户筛选 */}
          <div className={styles.filterItem}>
            <label className={styles.filterLabel}>上传者</label>
            <select
              value={filterUploader}
              onChange={(e) => setFilterUploader(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="all">全部用户</option>
              {uploaders.map((uploader) => (
                <option key={uploader} value={uploader}>
                  {uploader}
                </option>
              ))}
            </select>
          </div>
          {/* 上传时间区间筛选 */}
          <div className={styles.filterItem}>
            <label className={styles.filterLabel}>上传时间</label>
            <div className={styles.dateRange}>
              <input
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                className={styles.dateInput}
                placeholder="开始日期"
              />
              <span className={styles.dateSeparator}>至</span>
              <input
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                className={styles.dateInput}
                placeholder="结束日期"
              />
            </div>
          </div>
          {/* 可见性筛选 */}
          <div className={styles.filterItem}>
            <label className={styles.filterLabel}>可见性</label>
            <select
              value={filterVisibility}
              onChange={(e) => setFilterVisibility(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="all">全部可见性</option>
              <option value="0">私有</option>
              <option value="2">公开</option>
            </select>
          </div>
          {/* 类型筛选（仅知识传承） */}
          {!isExam && (
            <div className={styles.filterItem}>
              <label className={styles.filterLabel}>类型</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">全部类型</option>
                {KNOWLEDGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                <option value="general">来自学习考试</option>
              </select>
            </div>
          )}
          {/* 清除所有筛选按钮 */}
          <button
            className={styles.clearFiltersBtn}
            onClick={() => {
              setFilterUploader('all');
              setFilterStartDate('');
              setFilterEndDate('');
              setFilterVisibility('all');
              setFilterType('all');
              setSearchQuery('');
            }}
            disabled={filterUploader === 'all' && !filterStartDate && !filterEndDate && filterVisibility === 'all' && filterType === 'all' && !searchQuery}
          >
            <RotateCcw size={14} />
            清除筛选
          </button>
        </div>
      </div>

      {/* 地址栏：返回上一级 + 面包屑（仅知识传承模式，第二行） */}
      {!isExam && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <button
            onClick={() => setCurrentFolderId(breadcrumb.length >= 2 ? breadcrumb[breadcrumb.length - 2].folder_id : null)}
            disabled={currentFolderId === null}
            title="返回上一级"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30,
              border: '1px solid #e5e7eb', borderRadius: 8, background: currentFolderId === null ? '#f9fafb' : '#fff',
              color: currentFolderId === null ? '#d1d5db' : '#374151', cursor: currentFolderId === null ? 'default' : 'pointer',
            }}>
            <ArrowUp size={16} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: '.8125rem', color: '#6b7280', flexWrap: 'wrap' }}>
            <span style={{ cursor: 'pointer', color: currentFolderId === null ? '#111827' : '#2563eb', fontWeight: currentFolderId === null ? 600 : 400 }}
              onClick={() => setCurrentFolderId(null)}>全部文件</span>
            {breadcrumb.map((f, i) => (
              <span key={f.folder_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                <ChevronRight size={13} color="#9ca3af" />
                <span
                  style={{ cursor: 'pointer', color: i === breadcrumb.length - 1 ? '#111827' : '#2563eb', fontWeight: i === breadcrumb.length - 1 ? 600 : 400 }}
                  onClick={() => setCurrentFolderId(f.folder_id)}>
                  {f.folder_name}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}


      {/* 批量操作工具栏 */}
      {canEdit && selectedArchives.length > 0 && (
        <div className={styles.batchToolbar}>
          <div className={styles.batchInfo}>
            <Check size={18} />
            已选择 <span className={styles.batchCount}>{selectedArchives.length}</span> 个档案
          </div>
          <div className={styles.batchActions}>
            <button
              type="button"
              className={styles.selectAllBtn}
              onClick={selectAllArchives}
            >
              {paginatedArchives.length > 0 && paginatedArchives.every(archive => selectedArchives.includes(archive.archive_id)) ? '取消全选' : '全选当前页'}
            </button>
            <button
              type="button"
              className={styles.batchDeleteBtn}
              onClick={openBatchDeleteModal}
            >
              <Trash2 size={16} />
              批量删除
            </button>
            <button
              type="button"
              className={styles.cancelBatchBtn}
              onClick={() => setSelectedArchives([])}
            >
              取消选择
            </button>
          </div>
        </div>
      )}

      {/* 列表 */}
      <div className={styles.tableContainer}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
          <thead>
            <tr>
              {canEdit && (
                <th className={`${styles.tableHeaderCell}`} style={{ width: '50px' }}>
                  <label className={styles.checkboxWrapper}>
                    <input
                      type="checkbox"
                      checked={paginatedArchives.length > 0 && paginatedArchives.every(archive => selectedArchives.includes(archive.archive_id))}
                      onChange={selectAllArchives}
                    />
                    <span className={styles.checkboxCustom}>
                      <Check size={14} strokeWidth={3} />
                    </span>
                  </label>
                </th>
              )}
              <th className={styles.tableHeaderCell} style={{ width: '200px', textAlign: 'center' }}>名称</th>
              {!isExam && (
                <th className={styles.tableHeaderCell} style={{ width: '100px' }}>类型</th>
              )}
              <th className={styles.tableHeaderCell} style={{ width: '100px' }}>创建者</th>
              <th className={styles.tableHeaderCell} style={{ width: '120px' }}>创建时间</th>
              <th className={styles.tableHeaderCell} style={{ width: '80px' }}>文件数</th>
              <th className={styles.tableHeaderCell} style={{ width: '100px' }}>可见性</th>
              {showQualityFeatures && (
                <th className={styles.tableHeaderCell} style={{ width: '100px' }}>解析状态</th>
              )}
              <th className={styles.tableHeaderCell} style={{ width: '120px', textAlign: 'center' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {/* 文件夹行（在档案行之前，Windows 详细信息视图风格） */}
            {!isExam && !searchQuery && folders.map(f => (
              <tr key={`folder-${f.folder_id}`} className={styles.tableRow} style={{ background: '#fdfcf7' }}>
                {canEdit && <td className={`${styles.tableCell} ${styles.checkbox}`} />}
                <td className={`${styles.tableCell} ${styles.name} ${styles.cardHeaderCell}`}>
                  <div
                    className={styles.archiveNameWithExpand}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setCurrentFolderId(f.folder_id)}
                    onDoubleClick={() => setCurrentFolderId(f.folder_id)}
                    title={`打开文件夹「${f.folder_name}」`}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: 8, flexShrink: 0 }}>
                      <WinFolderIcon size={22} />
                    </span>
                    <span className={styles.archiveName} style={{ fontWeight: 600 }}>{f.folder_name}</span>
                  </div>
                </td>
                <td className={`${styles.tableCell} ${styles.status}`} data-label="类型">
                  <span style={{ fontSize: '.6875rem', fontWeight: 500, padding: '2px 8px', borderRadius: 999, background: '#fef9c3', color: '#854d0e' }}>文件夹</span>
                </td>
                <td className={`${styles.tableCell} ${styles.meta}`} data-label="创建者">—</td>
                <td className={`${styles.tableCell} ${styles.meta}`} data-label="创建时间">
                  {formatDate(f.create_time || f.update_time)}
                </td>
                <td className={`${styles.tableCell} ${styles.meta}`} data-label="文件数">{f.archive_count} 个档案</td>
                <td className={`${styles.tableCell} ${styles.status}`} data-label="可见性">—</td>
                <td className={`${styles.tableCell} ${styles.status}`} data-label="解析状态">—</td>
                <td className={`${styles.tableCell} ${styles.actions} ${styles.cardFooterCell}`} data-label="操作">
                  {canEdit && (
                    <div className={styles.tableActions}>
                      <button className={styles.tableActionBtn} title="重命名"
                        onClick={() => { setFolderForm({ folder_name: f.folder_name, parent_id: f.parent_id }); setFolderModal({ mode: 'rename', folder: f }); }}>
                        <PencilIcon size={15} />
                      </button>
                      <button className={styles.tableActionBtn} title="删除" style={{ color: '#dc2626' }}
                        onClick={() => handleFolderDelete(f)}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {paginatedArchives.map((archive) => {
              const isSelected = selectedArchives.includes(archive.archive_id);
              return (
                <React.Fragment key={archive.archive_id}>
                <tr
                  className={`${styles.tableRow} ${isSelected ? styles.selected : ''}`}
                  style={(archive as any).is_archived === 1 ? { opacity: 0.55 } : undefined}
                >
                  {canEdit && (
                    <td className={`${styles.tableCell} ${styles.checkbox}`}>
                      <label className={styles.checkboxWrapper}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleArchiveSelection(archive.archive_id)}
                        />
                        <span className={styles.checkboxCustom}>
                          <Check size={14} strokeWidth={3} />
                        </span>
                      </label>
                    </td>
                  )}
                  <td className={`${styles.tableCell} ${styles.name} ${styles.cardHeaderCell}`} title={archive.archive_name} data-label="名称">
                    <div className={styles.archiveNameWithExpand}>
                      <button
                        className={styles.expandBtn}
                        onClick={() => toggleArchiveExpand(archive.archive_id)}
                        title={expandedArchives.includes(archive.archive_id) ? '收起文件' : '展开文件'}
                      >
                        {expandedArchives.includes(archive.archive_id) ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </button>
                      <span className={styles.archiveName}>{archive.archive_name}</span>
                      {!isExam && (archive as any).is_archived === 1 && (
                        <span style={{ fontSize: '.6875rem', padding: '1px 7px', borderRadius: 999, background: '#f3f4f6', color: '#6b7280', marginLeft: 6, verticalAlign: 'middle' }}>已归档</span>
                      )}
                      {!isExam && searchQuery && (archive as any).folder_path && (
                        <span title="所在文件夹" style={{ fontSize: '.6875rem', padding: '1px 7px', borderRadius: 999, background: '#fefce8', color: '#a16207', marginLeft: 6, verticalAlign: 'middle' }}>
                          {(archive as any).folder_path}
                        </span>
                      )}
                    </div>
                  </td>
                  {!isExam && (
                    <td className={`${styles.tableCell} ${styles.status}`} data-label="类型">
                      {(() => {
                        const at = ((archive as any).archive_type || '') as string;
                        const shared = (archive as any).shared_to_knowledge === 1;
                        const isGeneral = at === 'general';
                        return (
                          <span
                            title={isGeneral ? (shared ? '来自学习考试·已共享到知识传承' : '学习档案') : '档案类型'}
                            style={{
                              display: 'inline-flex', alignItems: 'center',
                              fontSize: '.6875rem', fontWeight: 500,
                              padding: '2px 8px', borderRadius: 999,
                              background: isGeneral ? '#f0fdf4' : '#eff6ff',
                              color: isGeneral ? '#16a34a' : '#2563eb',
                            }}
                          >
                            {isGeneral ? '学习档案' : (TYPE_LABELS[at] || at)}
                          </span>
                        );
                      })()}
                    </td>
                  )}
                  <td className={`${styles.tableCell} ${styles.meta}`} data-label="创建者">
                    {archive.uploader_name || '未知'}
                  </td>
                  <td className={`${styles.tableCell} ${styles.meta}`} data-label="创建时间">
                    {formatDate(archive.upload_time)}
                  </td>
                  <td className={`${styles.tableCell} ${styles.meta}`} data-label="文件数">
                    {archive.files?.length || 0}
                  </td>
                  <td className={`${styles.tableCell} ${styles.status}`} data-label="可见性">
                    <span className={`${styles.visibilityIndicator} ${
                      archive.visibility === 0 ? styles.visibilityPrivate :
                      styles.visibilityPublic
                    }`}>
                      {archive.visibility === 0 ? '私有' : '公开'}
                    </span>
                  </td>
                  {showQualityFeatures && (
                    <td className={`${styles.tableCell} ${styles.status}`} data-label="解析状态">
                      {(() => {
                        const st = archive.process_status ?? 0;
                        const cfg = {
                          0: { label: '未解析', color: '#6b7280', bg: '#f3f4f6', tip: '尚未解析，点击操作列“AI 解析”开始' },
                          1: { label: '解析中', color: '#2563eb', bg: '#eff6ff', tip: '正在解析并向量化…' },
                          2: { label: '已解析', color: '#16a34a', bg: '#f0fdf4', tip: archive.process_time ? `解析完成于 ${new Date(archive.process_time).toLocaleString('zh-CN')}，可被问答检索` : '解析完成，可被问答检索' },
                          3: { label: '解析失败', color: '#dc2626', bg: '#fef2f2', tip: archive.process_error || '解析失败，可重新点击“AI 解析”' },
                        }[st] || { label: '未解析', color: '#6b7280', bg: '#f3f4f6', tip: '' };
                        return (
                          <span
                            title={cfg.tip}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                              fontSize: '.6875rem', fontWeight: 500,
                              color: cfg.color, background: cfg.bg,
                              padding: '2px 8px', borderRadius: 999,
                            }}
                          >
                            {st === 1 && <Loader2 size={11} className={styles.spin} />}
                            {cfg.label}
                          </span>
                        );
                      })()}
                    </td>
                  )}
                  <td className={`${styles.tableCell} ${styles.actions} ${styles.cardFooterCell}`} data-label="操作">
                    <div className={styles.tableActions}>
                      {/* 编辑和删除按钮 - 仅上传者可用，非上传者点击提示权限不足 */}
                      {(() => {
                        const isCreator = archive.uploader_id === user?.user_id || user?.role_id === 8 || user?.role_name === '超级管理员';
                        return (
                          <>
                            {showQualityFeatures && space === 'quality' && (
                              <button className={styles.tableActionBtn}
                                onClick={() => handleShowDetail(archive.archive_id)}
                                title={((archive as any).extraction_status >= 2) ? '查看解析结果' : '查看详情（尚未解析）'}
                                style={{ color: (archive as any).extraction_status >= 2 ? '#3b82f6' : '#9ca3af' }}>
                                <Eye size={16}/>
                              </button>
                            )}

                            {showQualityFeatures && isCreator && (() => {
                              // 合并后的 AI 解析按钮：按档案类型分发处理方式
                              // - 质量事故 → 结构化提取（七大分区）；若知识处理未完成则同时补触发
                              // - 日常档案 → 知识处理（分块 + 向量化，供问答检索）
                              const at = ((archive as any).archive_type || '') as string;
                              const isQuality = at === 'quality_incident';
                              const busy = isQuality
                                ? extractingId === archive.archive_id
                                : archive.process_status === 1;
                              const title = isQuality
                                ? 'AI 解析（提取质量事故结构化信息，确认后入库）'
                                : archive.process_status === 1
                                  ? 'AI 解析中（分块与向量化）...'
                                  : archive.process_status === 2
                                    ? 'AI 解析（重新解析，更新知识库）'
                                    : 'AI 解析（解析并向量化，完成后可 AI 问答）';
                              return (
                                <button className={styles.tableActionBtn}
                                  onClick={() => {
                                    if (isQuality) {
                                      // 质量档案两类处理都需要：知识处理未完成时先补触发（后台自动），再打开结构化提取
                                      if (archive.process_status !== 2) handleProcessArchive(archive.archive_id);
                                      openExtractModal(archive.archive_id);
                                    } else {
                                      handleProcessArchive(archive.archive_id);
                                    }
                                  }}
                                  disabled={busy}
                                  title={title}
                                  style={{ color: busy ? '#9ca3af' : '#8b5cf6' }}>
                                  {busy ? <Loader2 size={16} className={styles.spin}/> : <Sparkles size={16}/>}
                                </button>
                              );
                            })()}
                            <button
                              className={`${styles.tableActionBtn} ${!isCreator ? styles.disabled : ''}`}
                              onClick={() => {
                                if (!isCreator) {
                                  showToast('error', '权限不足：仅档案创建者可编辑');
                                  return;
                                }
                                handleEdit(archive);
                              }}
                              title={isCreator ? '编辑' : '权限不足（非创建者）'}
                            >
                              <Edit size={16} strokeWidth={1.5} />
                            </button>
                            <button
                              className={`${styles.tableActionBtn} ${styles.danger} ${!isCreator ? styles.disabled : ''}`}
                              onClick={() => {
                                if (!isCreator) {
                                  showToast('error', '权限不足：仅档案创建者可删除');
                                  return;
                                }
                                handleDelete(archive.archive_id);
                              }}
                              title={isCreator ? '删除' : '权限不足（非创建者）'}
                            >
                              <Trash2 size={16} strokeWidth={1.5} />
                            </button>
                            {!isExam && isCreator && (
                              <button className={styles.tableActionBtn}
                                onMouseEnter={(e) => openMoreMenu(archive.archive_id, e)}
                                onMouseLeave={scheduleCloseMoreMenu}
                                title="更多操作"
                                style={{ color: '#6b7280' }}>
                                <MoreVertical size={16} />
                              </button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
                {/* 展开的文件列表 */}
                {expandedArchives.includes(archive.archive_id) && archive.files && archive.files.length > 0 && (
                  <tr className={styles.expandedFileRow}>
                    <td colSpan={canEdit ? 7 : 6} className={styles.expandedFileCell}>
                      <motion.div
                        className={styles.fileListContainer}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                      >
                        <div className={styles.fileList}>
                          {archive.files.map((file, index) => (
                            <div key={file.file_id} className={styles.fileItem}>
                              <div className={styles.fileTreeLine}>
                                <span className={styles.fileTreeBranch}>├─</span>
                              </div>
                              <div className={styles.fileInfo}>
                                <span className={styles.fileName} title={file.file_name}>
                                  {file.file_name}
                                </span>
                                <div className={styles.fileMeta}>
                                  <span className={styles.fileType}>{file.file_type?.toUpperCase()}</span>
                                  <span className={styles.fileSize}>{formatSize(file.file_size)}</span>
                                  <span className={styles.fileUploadTime}>上传于 {formatDateSimple(file.upload_time)}</span>
                                </div>
                              </div>
                              <button
                                className={styles.filePreviewBtn}
                                onClick={async () => {
                                  try {
                                    if (file.preview_url) {
                                      window.open(file.preview_url, '_blank');
                                    } else {
                                      const res = await api.getFilePreviewUrl(file.file_id);
                                      if (res.code === 200 && res.data?.preview_url) {
                                        window.open(res.data.preview_url, '_blank');
                                      } else {
                                        showToast('error', '无法预览该文件');
                                      }
                                    }
                                  } catch (error) {
                                    showToast('error', '预览失败：' + getErrorMessage(error));
                                  }
                                }}
                                title="预览文件"
                              >
                                <Eye size={14} />
                              </button>
                              {!isExam && space === 'daily' && (
                                <button
                                  className={styles.filePreviewBtn}
                                  onClick={async () => {
                                    try {
                                      showToast('info', '开始下载...');
                                      await api.downloadKnowledgeFile(file.file_id);
                                    } catch (error) {
                                      showToast('error', getErrorMessage(error));
                                    }
                                  }}
                                  title="下载文件"
                                >
                                  <FileDown size={14} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* 翻页控件 */}
      <div className={styles.pagination}>
        <div className={styles.paginationInfo}>
          共 {filteredArchives.length} 条记录
        </div>
        <div className={styles.paginationControls}>
          <button
            className={`${styles.paginationBtn} ${currentPage === 1 ? styles.disabled : ''}`}
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            首页
          </button>
          <button
            className={`${styles.paginationBtn} ${currentPage === 1 ? styles.disabled : ''}`}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            上一页
          </button>
          <div className={styles.pageNumbers}>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  className={`${styles.pageNumber} ${currentPage === pageNum ? styles.active : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <span className={styles.pageEllipsis}>...</span>
            )}
            {totalPages > 5 && currentPage >= totalPages - 2 && (
              <button
                className={`${styles.pageNumber} ${currentPage === totalPages ? styles.active : ''}`}
                onClick={() => setCurrentPage(totalPages)}
              >
                {totalPages}
              </button>
            )}
          </div>
          <button
            className={`${styles.paginationBtn} ${currentPage === totalPages ? styles.disabled : ''}`}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            下一页
          </button>
          <button
            className={`${styles.paginationBtn} ${currentPage === totalPages ? styles.disabled : ''}`}
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            末页
          </button>
        </div>
      </div>

      {/* 空状态（文件夹行在表格内，不参与此判断则需一并考虑） */}
      {filteredArchives.length === 0 && (!isExam || folders.length === 0) && !loading && (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>
            <FileText size={48} strokeWidth={1} />
          </div>
          <p className={styles.emptyText}>
            {searchQuery ? '没有找到匹配的知识档案' : '暂无知识档案'}
          </p>
          {canEdit && !searchQuery && (
            <>
              {!isExam && (
                <motion.button
                  className={styles.createButton}
                  style={{ marginRight: 8, background: '#fff', color: '#374151', border: '1px solid #d1d5db' }}
                  onClick={() => { setFolderForm({ folder_name: '', parent_id: null }); setFolderModal({ mode: 'create' }); }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FolderPlus size={16} />
                  <span>新建文件夹</span>
                </motion.button>
              )}
              <motion.button
                className={styles.createButton}
                onClick={handleCreate}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Plus size={18} strokeWidth={2} />
                <span>新建档案</span>
              </motion.button>
            </>
          )}
        </div>
      )}

      {/* 创建/编辑弹窗 */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCancelEdit}
          >
            <motion.div
              className={editingArchive ? styles.modalLarge : styles.modal}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>
                  {editingArchive ? '编辑档案' : '新建档案'}
                </h2>
                <button className={styles.modalClose} onClick={handleCancelEdit}>
                  <X size={20} strokeWidth={1.5} />
                </button>
              </div>

              <form className={styles.modalBody} onSubmit={(e) => {
                // 只有在真正提交（点击保存按钮）时才执行默认提交行为
                // 防止意外的表单提交，如来自文件上传组件的提交
                if (!e.nativeEvent.submitter || e.nativeEvent.submitter.className?.includes('submitButton')) {
                  handleSubmit(e);
                } else {
                  e.preventDefault();
                }
              }}>
                <div className={styles.formGroup}>
                  <label>档案名称 *</label>
                  <input
                    type="text"
                    placeholder="请输入档案名称"
                    value={formData.archive_name}
                    onChange={(e) =>
                      setFormData({ ...formData, archive_name: e.target.value })
                    }
                    required
                  />
                </div>

                {/* 文件管理部分 - 移动到档案名称之后 */}
                {editingArchive && (
                  <div className={styles.formGroup}>
                    <label>
                      <Upload size={14} />
                      文件管理
                    </label>

                    {/* 文件上传区域 */}
                    <div className={styles.uploadSection}>
                      <div className={styles.uploadSectionTitle}>上传新文件</div>
                      <form onSubmit={(e) => e.preventDefault()}>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', lineHeight: '1.5' }}>
                          <FileText size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                          上传视频文件时需同步上传字幕文件（.srt），否则视频将无法上传。可使用剪映配置并导出字幕文件。
                        </div>
                        <FileUpload
                          archiveId={editingArchive.archive_id}
                          maxFileSize={3000}
                          onComplete={(uploadedFiles) => {
                            console.log('FileUpload onComplete called, uploadedFiles:', uploadedFiles);
                            if (editingArchive && uploadedFiles) {
                              // 追踪新上传的文件ID
                              const newFileIds = uploadedFiles.map(f => f.file_id);
                              console.log('新上传的文件IDs:', newFileIds);
                              setNewlyUploadedFiles(prev => {
                                const updated = [...prev, ...newFileIds];
                                console.log('更新后的newlyUploadedFiles:', updated);
                                return updated;
                              });
                              // 刷新文件列表
                              loadArchiveFiles(editingArchive.archive_id);
                            }
                          }}
                        />
                      </form>
                    </div>

                    {/* 已上传文件列表 */}
                    <div className={styles.existingFiles}>
                      <div className={styles.existingFilesHeader}>
                        <span>已上传文件 ({archiveFiles.length})</span>
                      </div>
                      {loadingFiles ? (
                        <div className={styles.loadingFilesInline}>
                          <Loader2 className={styles.spinner} size={16} />
                          <span>加载中...</span>
                        </div>
                      ) : archiveFiles.length === 0 ? (
                        <div className={styles.noFilesHint}>暂无已上传的文件</div>
                      ) : (
                        <div className={styles.fileList}>
                          {archiveFiles.map((file) => (
                            <div key={file.file_id} className={styles.fileItem}>
                              <div className={styles.fileItemIcon}>
                                {getFileIcon(file.file_type)}
                              </div>
                              <div className={styles.fileItemInfo}>
                                <span className={styles.fileItemName}>{file.file_name}</span>
                                <span className={styles.fileItemSize}>{formatSize(file.file_size)}</span>
                              </div>
                              <button
                                type="button"
                                className={styles.fileDeleteBtn}
                                onClick={() => handleDeleteFile(file.file_id, file.file_name)}
                                disabled={deletingFileId === file.file_id}
                                title="删除文件"
                              >
                                {deletingFileId === file.file_id ? (
                                  <Loader2 size={14} className={styles.spinner} />
                                ) : (
                                  <Trash2 size={14} />
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label>档案描述</label>
                  <textarea
                    placeholder="请输入档案描述"
                    value={formData.archive_desc}
                    onChange={(e) =>
                      setFormData({ ...formData, archive_desc: e.target.value })
                    }
                    rows={4}
                  />
                </div>

                <div className={styles.formGroup}>
                  {!isExam && (
                    <>
                      <label>档案类型</label>
                      <select
                        value={formData.archive_type}
                        onChange={(e) => {
                          const t = e.target.value;
                          setFormData({
                            ...formData,
                            archive_type: t,
                            visibility: editingArchive ? formData.visibility : (TYPE_DEFAULT_VIS[t] ?? 0),
                          });
                        }}
                        className={styles.visibilitySelect}
                        disabled={!!editingArchive && formData.archive_type === 'quality_incident'}
                      >
                        {KNOWLEDGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      {!!editingArchive && formData.archive_type === 'quality_incident' && (
                        <p className={styles.hintText} style={{ fontSize: '.75rem', color: '#9ca3af', margin: '4px 0 0' }}>
                          质量事故档案类型不可变更
                        </p>
                      )}
                      <div style={{ height: 14 }} />
                    </>
                  )}
                  {isExam && (
                    <>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.shared_to_knowledge === 1}
                          onChange={(e) => setFormData({ ...formData, shared_to_knowledge: e.target.checked ? 1 : 0 })}
                        />
                        共享到知识传承（开启后可在知识问答中被检索）
                      </label>
                      <div style={{ height: 14 }} />
                    </>
                  )}
                  {!isExam && (
                    <>
                      <label>所在文件夹</label>
                      <select
                        value={formData.folder_id ?? 0}
                        onChange={(e) => setFormData({ ...formData, folder_id: e.target.value === '0' ? null : Number(e.target.value) })}
                        className={styles.visibilitySelect}
                      >
                        <option value={0}>未分类（根目录）</option>
                        {allFolders.map(f => (
                          <option key={f.folder_id} value={f.folder_id}>{folderPathMap.get(f.folder_id) || f.folder_name}</option>
                        ))}
                      </select>
                      <div style={{ height: 14 }} />
                    </>
                  )}
                  <label>可见性设置</label>
                  <select
                    value={formData.visibility}
                    onChange={(e) => setFormData({...formData, visibility: parseInt(e.target.value)})}
                    className={styles.visibilitySelect}
                  >
                    <option value={0}>私有 - 仅自己可见</option>
                    <option value={2}>公开 - 同部门及考官可见</option>
                  </select>
                </div>


                <div className={styles.modalFooter}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={handleCancelEdit}
                  >
                    取消
                  </button>
                  <button type="submit" className={styles.submitButton} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 size={16} className={styles.spinner} />
                        保存中...
                      </>
                    ) : (
                      editingArchive ? '保存更改' : '创建档案'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 详情弹窗 */}
      <AnimatePresence>
        {showDetailModal && selectedArchive && (
          <ArchiveDetailModal
            archive={selectedArchive}
            onClose={() => setShowDetailModal(false)}
          />
        )}
      </AnimatePresence>

      {/* 删除文件确认弹窗 */}
      <AnimatePresence>
        {confirmModal.show && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeConfirmModal}
          >
            <motion.div
              className={styles.confirmModal}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.confirmIcon}>
                <AlertTriangle size={40} />
              </div>
              <h3 className={styles.confirmTitle}>确认删除</h3>
              <p className={styles.confirmMessage}>
                确定要删除文件「{confirmModal.fileName}」吗？此操作不可恢复。
              </p>
              <div className={styles.confirmActions}>
                <button className={styles.confirmCancel} onClick={closeConfirmModal}>
                  取消
                </button>
                <button
                  className={`${styles.confirmButton} ${styles.danger}`}
                  onClick={confirmDeleteFile}
                  disabled={deletingFileId === confirmModal.fileId}
                >
                  {deletingFileId === confirmModal.fileId ? (
                    <Loader2 size={16} className={styles.spinner} />
                  ) : (
                    '确认删除'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 批量删除确认弹窗 */}
      <AnimatePresence>
        {isBatchDeleteModalOpen && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeBatchDeleteModal}
          >
            <motion.div
              className={styles.confirmModal}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.confirmIcon}>
                <Trash2 size={40} />
              </div>
              <h3 className={styles.confirmTitle}>确认批量删除</h3>
              <p className={styles.confirmMessage}>
                确定要删除选中的 <strong>{selectedArchives.length}</strong> 个知识档案吗？
              </p>
              <p className={styles.confirmWarning}>
                此操作不可恢复，档案中的所有文件也将被删除，请谨慎操作。
              </p>
              <div className={styles.confirmActions}>
                <button className={styles.confirmCancel} onClick={closeBatchDeleteModal}>
                  取消
                </button>
                <button
                  className={`${styles.confirmButton} ${styles.danger}`}
                  onClick={handleBatchDelete}
                >
                  确认删除
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 浮动新增按钮（知识传承模式：展开 新建文件夹/新建档案 两个选项） */}
      {canEdit && (
        <>
          {!isExam && newMenuOpen && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setNewMenuOpen(false)} />
              <div style={{
                position: 'fixed', bottom: 102, left: 'calc(50% + 130px)', transform: 'translateX(-50%)', zIndex: 99,
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
                boxShadow: '0 12px 32px rgba(0,0,0,.16)', overflow: 'hidden', minWidth: 160,
              }}>
                <button
                  onClick={() => { setNewMenuOpen(false); setFolderForm({ folder_name: '', parent_id: null }); setFolderModal({ mode: 'create' }); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 18px', border: 'none', background: '#fff', cursor: 'pointer', fontSize: '.875rem', color: '#374151' }}>
                  <WinFolderIcon size={20} /> 新建文件夹
                </button>
                <div style={{ height: 1, background: '#f3f4f6' }} />
                <button
                  onClick={() => { setNewMenuOpen(false); handleCreate(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 18px', border: 'none', background: '#fff', cursor: 'pointer', fontSize: '.875rem', color: '#374151' }}>
                  <FileText size={18} color="#3b82f6" /> 新建档案
                </button>
              </div>
            </>
          )}
          <motion.button
            className={styles.fab}
            onClick={() => { if (isExam) handleCreate(); else setNewMenuOpen(v => !v); }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title={isExam ? '新建档案' : '新建（文件夹 / 档案）'}
          >
            <Plus size={28} strokeWidth={2} style={{ transform: newMenuOpen && !isExam ? 'rotate(45deg)' : 'none', transition: 'transform .2s' }} />
          </motion.button>
        </>
      )}

      <QualityExtractionModal
        showExtractionModal={showExtractionModal}
        showDetailModal={showDetailModal2}
        extractionData={extractionData}
        detailData={detailData}
        onCloseExtraction={() => setShowExtractionModal(false)}
        onCloseDetail={() => setShowDetailModal2(false)}
        onConfirm={handleConfirmExtraction}
      />
      {/* AI 提取确认/进度弹窗 */}
      <AnimatePresence>
        {showExtractModal && (
          <motion.div className={styles.modalOverlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={() => setShowExtractModal(false)}>
            <motion.div className={styles.modal} style={{maxWidth:560}} initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} exit={{opacity:0,scale:0.95}} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>
                  {extractModalStage === 'confirm' ? '确认 AI 解析' : extractModalStage === 'running' ? '正在 AI 解析...' : '解析完成'}
                </h2>
                <button className={styles.modalClose} onClick={() => { setShowExtractModal(false); }}>
                  <X size={20} strokeWidth={1.5} />
                </button>
              </div>
              <div style={{padding:'16px 20px'}}>
                {/* 阶段一：确认 */}
                {extractModalStage === 'confirm' && (
                  <>
                    <p style={{margin:'0 0 12px',fontSize:'14px',color:'#374151'}}>
                      将对档案「<strong>{extractModalArchiveName}</strong>」中的 {extractModalFiles.length} 个文件进行 AI 结构化解析：
                    </p>
                    <div style={{maxHeight:200,overflow:'auto',marginBottom:16,border:'1px solid #e5e7eb',borderRadius:8}}>
                      {extractModalFiles.map((f, i) => (
                        <div key={f.file_id} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',borderBottom:'1px solid #f3f4f6',fontSize:'13px'}}>
                          <span style={{color:'#9ca3af',minWidth:20}}>{i+1}.</span>
                          <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.file_name}</span>
                          <span style={{color:'#9ca3af',fontSize:'11px'}}>{f.file_type?.toUpperCase()} {formatSize(f.file_size)}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                      <button onClick={() => setShowExtractModal(false)} style={{padding:'8px 20px',border:'1px solid #d1d5db',borderRadius:8,background:'white',cursor:'pointer',fontSize:'14px'}}>取消</button>
                      <button onClick={confirmExtract} style={{padding:'8px 20px',border:'none',borderRadius:8,background:'#6366f1',color:'white',cursor:'pointer',fontSize:'14px',display:'flex',alignItems:'center',gap:6}}>
                        <Sparkles size={16} /> 开始解析
                      </button>
                    </div>
                  </>
                )}

                {/* 阶段二：进行中 */}
                {extractModalStage === 'running' && (
                  <div>
                    {extractProgress && (
                      <div style={{background:'linear-gradient(135deg,#667eea,#764ba2)',color:'white',padding:12,borderRadius:8,marginBottom:12,fontSize:14,textAlign:'center'}}>
                        {extractProgress}
                      </div>
                    )}
                    <div style={{maxHeight:200,overflow:'auto',border:'1px solid #e5e7eb',borderRadius:8}}>
                      {extractModalFiles.map((f, i) => (
                        <div key={f.file_id} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',borderBottom:'1px solid #f3f4f6',fontSize:'13px'}}>
                          <span style={{color:'#9ca3af',minWidth:20}}>{i+1}.</span>
                          <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.file_name}</span>
                          {i < extractCompletedFiles
                            ? <span style={{fontSize:'11px',color:'#10b981'}}>&#x2713; 完成</span>
                            : i === extractCompletedFiles
                              ? <Loader2 size={14} className={styles.spin} style={{color:'#6366f1'}} />
                              : <span style={{fontSize:'11px',color:'#d1d5db'}}>等待</span>
                          }
                        </div>
                      ))}
                    </div>
                    <p style={{textAlign:'center',color:'#9ca3af',fontSize:'12px',margin:'12px 0 0'}}>关闭弹窗后解析将在后台继续</p>
                  </div>
                )}

                {/* 阶段三：完成 */}
                {extractModalStage === 'done' && (
                  <div style={{textAlign:'center',padding:'20px 0'}}>
                    <div style={{fontSize:48,marginBottom:12}}>&#x2705;</div>
                    <p style={{fontSize:'14px',color:'#374151'}}>解析已完成，点击下方按钮查看结果</p>
                    <button onClick={() => { setShowExtractModal(false); setShowDetailModal2(true); }}
                      style={{marginTop:12,padding:'8px 20px',border:'none',borderRadius:8,background:'#6366f1',color:'white',cursor:'pointer',fontSize:'14px'}}>
                      查看解析结果
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* 文件夹新建/重命名/移动弹窗 */}
      {folderModal && (
        <div className={styles.modalOverlay} onClick={() => setFolderModal(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className={styles.modalHeader}>
              <h2>
                {folderModal.mode === 'create' ? '新建文件夹' : folderModal.mode === 'rename' ? `重命名「${folderModal.folder?.folder_name}」` : `移动「${folderModal.folder?.folder_name}」`}
              </h2>
              <button className={styles.modalClose} onClick={() => setFolderModal(null)}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              {folderModal.mode !== 'move' && (
                <div className={styles.formField}>
                  <label>文件夹名称</label>
                  <input
                    autoFocus
                    value={folderForm.folder_name}
                    onChange={(e) => setFolderForm({ ...folderForm, folder_name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleFolderSave(); }}
                    maxLength={100}
                    placeholder="如：制度类、2026新规"
                  />
                </div>
              )}
              {folderModal.mode === 'move' && (
                <div className={styles.formField}>
                  <label>移动到</label>
                  <select
                    value={folderForm.parent_id ?? 0}
                    onChange={(e) => setFolderForm({ ...folderForm, parent_id: e.target.value === '0' ? null : Number(e.target.value) })}
                  >
                    <option value={0}>根目录</option>
                    {allFolders
                      .filter(f => f.folder_id !== folderModal.folder?.folder_id)
                      .map(f => <option key={f.folder_id} value={f.folder_id}>{folderPathMap.get(f.folder_id) || f.folder_name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, padding: '14px 20px', borderTop: '1px solid #f0f0f0' }}>
              <button className={styles.folderModalCancel} onClick={() => setFolderModal(null)}>取消</button>
              <button
                className={styles.folderModalSave}
                disabled={folderSaving || (folderModal.mode !== 'move' && !folderForm.folder_name.trim())}
                onClick={handleFolderSave}
              >
                {folderSaving ? <Loader2 size={15} className={styles.spin} style={{ marginRight: 4 }} /> : null}
                {folderModal.mode === 'create' ? '创建' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* 档案移动弹窗 */}
      {moveArchive && (
        <div className={styles.modalOverlay} onClick={() => setMoveArchive(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className={styles.modalHeader}>
              <h2>移动档案「{moveArchive.archive_name}」</h2>
              <button className={styles.modalClose} onClick={() => setMoveArchive(null)}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formField}>
                <label>移动到文件夹</label>
                <select
                  value={moveTargetFolder ?? 0}
                  onChange={(e) => setMoveTargetFolder(e.target.value === '0' ? null : Number(e.target.value))}
                  className={styles.visibilitySelect}
                  style={{ width: '100%' }}
                >
                  <option value={0}>未分类（根目录）</option>
                  {allFolders.map(f => (
                    <option key={f.folder_id} value={f.folder_id}>{folderPathMap.get(f.folder_id) || f.folder_name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, padding: '14px 20px', borderTop: '1px solid #f0f0f0' }}>
              <button className={styles.folderModalCancel} onClick={() => setMoveArchive(null)}>取消</button>
              <button className={styles.folderModalSave} disabled={moveSaving} onClick={handleMoveArchive}>
                {moveSaving ? <Loader2 size={15} className={styles.spin} style={{ marginRight: 4 }} /> : null}
                移动
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 档案“更多”悬浮菜单（下载/归档） */}
      {moreMenu && (() => {
        const target = archives.find(a => a.archive_id === moreMenu.id);
        if (!target) return null;
        const archived = (target as any).is_archived === 1;
        return (
          <div
            style={{
              position: 'fixed', left: moreMenu.x, top: moreMenu.y, zIndex: 1000,
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,.14)', minWidth: 124, overflow: 'hidden',
            }}
            onMouseEnter={cancelCloseMoreMenu}
            onMouseLeave={scheduleCloseMoreMenu}
          >
            {space === 'daily' && (
              <button
                onClick={() => { setMoreMenu(null); handleDownloadArchive(target); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 14px', border: 'none', background: '#fff', cursor: 'pointer', fontSize: '.8125rem', color: '#374151' }}
              >
                <FileDown size={15} color="#0891b2" /> 下载档案
              </button>
            )}
            <button
              onClick={() => { setMoreMenu(null); setMoveTargetFolder((target as any).folder_id ?? null); setMoveArchive(target); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 14px', border: 'none', background: '#fff', cursor: 'pointer', fontSize: '.8125rem', color: '#374151' }}
            >
              <FolderInput size={15} color="#2563eb" /> 移动到...
            </button>
            <button
              onClick={() => { setMoreMenu(null); handleToggleArchive(target); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 14px', border: 'none', background: '#fff', cursor: 'pointer', fontSize: '.8125rem', color: '#374151' }}
            >
              {archived ? <ArchiveRestore size={15} color="#16a34a" /> : <Archive size={15} color="#92400e" />}
              {archived ? '恢复归档' : '归档'}
            </button>
          </div>
        );
      })()}

    </div>
  );
}

// 知识档案详情弹窗组件
function ArchiveDetailModal({
  archive,
  onClose,
}: {
  archive: ArchiveWithTags;
  onClose: () => void;
}) {
  const [files, setFiles] = useState<KnowledgeArchiveFile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFiles();
  }, [archive.archive_id]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const res = await api.getArchiveFiles(archive.archive_id);
      if (res.code === 200 && res.data) {
        setFiles(res.data);
      }
    } catch (error) {
      console.error('加载文件失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (file: KnowledgeArchiveFile) => {
    if (file.preview_url) {
      window.open(file.preview_url.replace('/minio/', '/internal-minio/'), '_blank');
    }
  };

  return (
    <motion.div
      className={styles.modalOverlay}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className={styles.modalLarge}
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{archive.archive_name}</h2>
          <button className={styles.modalClose} onClick={onClose}>
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        <div className={styles.detailContent}>
          {/* 基本信息 */}
          <div className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>基本信息</h3>
            <div className={styles.detailInfo}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>描述</span>
                <span className={styles.detailValue}>{archive.archive_desc || '暂无描述'}</span>
              </div>
            </div>
          </div>

          {/* 文件列表 */}
          <div className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>文件列表 ({files.length})</h3>

            {loading ? (
              <div className={styles.loadingFiles}>
                <Loader2 className={styles.spinner} size={24} />
                <span>加载文件...</span>
              </div>
            ) : files.length === 0 ? (
              <div className={styles.emptyFiles}>
                <FileText size={32} strokeWidth={1} />
                <span>暂无文件</span>
              </div>
            ) : (
              <div className={styles.fileGrid}>
                {files.map((file) => (
                  <motion.div
                    key={file.file_id}
                    className={styles.fileCard}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => handlePreview(file)}
                  >
                    <div className={styles.fileCardIcon}>
                      {getFileIcon(file.file_type)}
                    </div>
                    <div className={styles.fileCardInfo}>
                      <span className={styles.fileCardName}>{file.file_name}</span>
                      <div className={styles.fileCardMeta}>
                        <span className={styles.fileCardSize}>{formatSize(file.file_size)}</span>
                        <span className={styles.fileCardUploadTime}>上传于 {formatDateSimple(file.upload_time)}</span>
                      </div>
                    </div>
                    <div className={styles.fileCardAction}>
                      <Eye size={16} />
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}